import { v4 as uuidv4 } from 'uuid'
import has from 'lodash/has';
import fb from "./firebase";
import firebase from "firebase/compat/app";
import {Filesystem, Directory} from "@capacitor/filesystem";
import {Device} from "@capacitor/device";
import {debug} from "../config";


export interface Acervo {
  [uuid:string]: StoredItem;
}

export interface StoredItem {
  path: string;
  uploaded: boolean;
  uploading: boolean;
  contentType: string;
}

export interface AcervoItem extends StoredItem {
  uuid: string;
  url: string;
  fileName: string;
  errorUploading: boolean;
  error: string;
}

const contentTypes: {[ext: string]: string} = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  pdf: ' application/pdf',
  _default_: 'application/octet-stream',
}


export const acervo = {
  load: (): Acervo => {
    const files = localStorage.getItem('acervo');
    if (!files)
      return {};
    return JSON.parse(files);
  },

  save: (data: any, extension: string='jpg', contentType= 'image/jpeg'): Promise<AcervoItem> => {
    return new Promise(async (resolve) => {
      const files = acervo.load();

      const invalidExtension = /\W/.exec(extension);
      if (invalidExtension) {
        throw Error(`Invalid extension: ${extension}`);
      }

      const uuid = uuidv4();
      const fileName = `${uuid}.${extension}`;

      await Filesystem.writeFile({
        path: fileName,
        data,
        directory: Directory.Data,
      });

      const fileUri = await Filesystem.getUri({
        directory: Directory.Data,
        path: fileName,
      });

      localStorage.setItem('acervo', JSON.stringify({
        ...files,
        [uuid]: {path: fileUri.uri, uploaded: false, contentType},
      }));

      const item = await acervo.item(uuid);
      if (!item)
        throw Error("Erro ao obter item do acervo.");

      resolve(item);
    })
  },

  item: async (uuid: string, forceRefresh=false): Promise<AcervoItem | null> => {
    return new Promise(async (resolve) => {
      const files = acervo.load();

      if (!has(files, uuid))
        return resolve(null);

      const item: StoredItem = files[uuid];

      // verificar plataforma
      const info = await Device.getInfo();

      if (forceRefresh)
          return resolve(null);

      // converter URI para formato capacitor
      let url;

      if (info.platform === 'ios') {
        url = item.path.replace(
          /^file:\/\//,
          'capacitor://localhost/_capacitor_file_')
      } else if (info.platform === 'web') {
        url = '';
      }
      else {
        url = item.path.replace(
          /^file:\/\//,
          'http://localhost/_capacitor_file_')
      }

      const ext = acervo.getExtensionFromPath(item.path);

      // fix contentType
      const {contentType} = item;
      if (!contentType)
        item.contentType = acervo.getContentTypeFromExtension(ext);

      resolve({
        ...item,
        uuid,
        url,
        fileName: `${uuid}.${ext}`,
        errorUploading: false,
        error: '',
      });
    });
  },

  setAttr: (uuid: string, attribute:any, value:any) => {
    const files = acervo.load();
    const item = files[uuid];

    localStorage.setItem('acervo', JSON.stringify({
      ...files,
      [uuid]: {
        ...item,
        [attribute]: value,
      },
    }));
  },

  _onUploadNext: (s: firebase.storage.UploadTaskSnapshot, item: AcervoItem, progressCallback: (progress: number) => void) => {
    if (s.state === 'runing' && !item.uploading) {
      item.uploading = true;
      acervo.setAttr(item.uuid, 'uploading', true);
    }

    const progress = (s.bytesTransferred / s.totalBytes) * 100;
    progressCallback(progress);
  },

  _onUploadError: (error: firebase.storage.FirebaseStorageError, item: AcervoItem, errorCallback: (message: string) => void) => {
    acervo.setAttr(item.uuid, 'errorUploading', true);
    acervo.setAttr(item.uuid, 'error', error);
    errorCallback(error.message)
  },

  _onUploadComplete: (item: AcervoItem, successCallback: () => void) => {
    acervo.setAttr(item.uuid, 'uploaded', true);
    acervo.setAttr(item.uuid, 'uploading', false);
    item.uploading = false;
    item.uploaded = true;
    successCallback();
  },

  upload: async (item: AcervoItem,
           progressCallback: (progress:number) => void,
           errorCallback: (error:string) => void,
           successCallback: () => void) => {
    try {
      const res = await Filesystem.readFile({path: item.path});
      const {contentType} = item;

      // convert res.data to Blob if res.data is base64
      const blob = b64toBlob(res.data as string, contentType);
      fb.files.ref().child(item.fileName).put(blob, {contentType})
        .on('state_changed',
          s => {
            acervo._onUploadNext(s, item, progressCallback)
          },
          error => {
            acervo._onUploadError(error, item, errorCallback)
          },
          () => {
            acervo._onUploadComplete(item, successCallback)
          },
        );
    } catch (error: any) {
      errorCallback(error.toString());
      console.log(`Erro upload item: ${item}`);
    }
  },

  getExtensionFromPath: (path: string): string => {
    const match = /\.(\w+)$/.exec(path);

    if (match) {
      return match[1].toLowerCase();
    }

    debug('getExtensionFromPath extension not found', path);
    return "jpg";
  },

  getContentTypeFromExtension: (extension: string): string => {
    if (extension && has(contentTypes, extension))
      return contentTypes[extension];

    return contentTypes._default_;
  }
};


export const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, {type: contentType});
}